<template>
  <div
    class="inline-calendar calendar-primary justify-content-center mb-0"
  >
    <ul
      v-dragscroll.x
      @dragscrollmove="disableDateSelection"
      @dragscrollend="enableDateSelection"
      ref="datesWrapper"
      class="inline-calendar__dates"
      :style="dateItemsStyles"
    >
      <the-observer
        v-if="showFirstObserver"
        :class="`inline-calendar__date date-item`"
        @intersect="getPrevDatesInRange(minDate, daysRange, true)"
        :options="prevObserverOptions"
      />
      <li
        v-for="(date, index) in datesReadable"
        :key="`date-item_${index}`"
        :class="[{
          'in-range': date.isInRange,
          today: date.isToday,
          disabled: date.isDisabled,
          active: date.isActive,
        }, `inline-calendar__date date-item`]"
        :style="[date.isActive ? {backgroundColor: getWidgetSettings.main_color, borderColor: getWidgetSettings.main_color, color: '#fff'} : {}, date.isToday ? {borderColor: getWidgetSettings.main_color, color: getWidgetSettings.main_color} : {}]"
        @click.stop="dateClickHandler(date.date)"
      >
        <p v-if="showYear" class="date-item__year">{{ date.year }}</p>

        <p v-if="showMonth" class="date-item__month">{{ date.month }}</p>

        <p class="date-item__day display-2">{{ date.day }}</p>

        <p v-if="showWeekday" class="date-item__weekday">{{ date.weekday }}</p>
      </li>
      <the-observer
        v-if="showLastObserver"
        :class="`inline-calendar__date date-item`"
        @intersect="getNextDatesInRange(maxDate, daysRange, true)"
        :options="nextObserverOptions"
      />
    </ul>
  </div>
</template>
<script>
import { dragscroll } from 'vue-dragscroll'
import { mapGetters } from 'vuex'
import checkSameDay from '@/components/booking/calendars/inline/helpers/check-same-day'
import checkIsWeekend from '@/components/booking/calendars/inline/helpers/check-is-weekend'
import checkIsInRange from '@/components/booking/calendars/inline/helpers/check-is-in-range'
import isNotWorkingDay from '@/components/booking/calendars/inline/helpers/check-is-non-working-day'
import TheObserver from '@/components/booking/calendars/inline/TheObserver.vue'

export default {
  name: 'Calendar',
  components: {
    TheObserver,
  },
  directives: {
    dragscroll,
  },
  props: {
    initialDate: {
      type: Date,
      default: null,
    },
    initialRange: {
      type: Object,
      default: null,
      validator: value => (
        Object.prototype.hasOwnProperty.call(value, 'startDate') && Object.prototype.hasOwnProperty.call(value, 'endDate') && value.startDate instanceof Date && value.endDate instanceof Date
      ),
    },
    daysRange: {
      type: Number,
      default: 7,
    },
    itemWidth: {
      type: Number,
      default: 80,
    },
    itemsGap: {
      type: Number,
      default: 10,
    },
    locale: {
      type: String,
      default: 'en-US',
    },
    scrollSpeed: {
      type: Number,
      default: 4,
    },
    workspace: {
      type: Object,
      required: true,
    },
    specMinDate: {
      type: Date,
      default: null,
    },
    specMaxDate: {
      type: Date,
      default: null,
    },
    disablePastDays: {
      type: Boolean,
      default: false,
    },
    disableNextDays: {
      type: Boolean,
      default: false,
    },
    isRange: {
      type: Boolean,
      default: false,
    },
    showYear: {
      type: Boolean,
      default: true,
    },
    showMonth: {
      type: Boolean,
      default: true,
    },
    showWeekday: {
      type: Boolean,
      default: true,
    },
    enableMousewheelScroll: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      dates: [],
      canSelectDate: true,
      selectedDate: this.initialDate,
      minDate: null,
      maxDate: null,
      startDate: this.initialRange?.startDate || null,
      endDate: this.initialRange?.endDate || null,
      showFirstObserver: true,
      showLastObserver: true,
      windowWidth: window.innerWidth,
    }
  },
  computed: {
    ...mapGetters(['getWidgetSettings']),
    datesReadable() {
      return this.dates.map(date => ({
        date,
        month: new Date(date).toLocaleDateString(this.locale, {
          month: 'short',
        }),
        year: new Date(date).getFullYear(),
        weekday: new Date(date).toLocaleDateString(this.locale, {
          weekday: 'short',
        }),
        day: new Date(date).getDate(),
        isToday: checkSameDay(date, new Date()),
        isWeekend: checkIsWeekend(new Date(date)),
        isActive:
          (this.selectedDate && checkSameDay(date, this.selectedDate)) || (this.startDate && checkSameDay(date, this.startDate)) || (this.endDate && checkSameDay(date, this.endDate)),
        ...(this.isRange && { isInRange: checkIsInRange(date, this.startDate, this.endDate) }),
        isDisabled:
          isNotWorkingDay(new Date(date), this.workspace.iso_weekday_ids, this.workspace.holidays_by_workspace, this.workspace.days_off_by_workspace) || (this.disablePastDays && date < new Date()) || (this.disableNextDays && date > new Date()),
      }))
    },
    dateItemsStyles() {
      return {
        gridAutoColumns: `${this.itemWidth}px`,
        gridColumnGap: `${this.itemsGap}px`,
      }
    },
    prevObserverOptions() {
      return {
        threshold: 0,
        root: this.$refs.datesWrapper,
        rootMargin: `0px 0px 0px ${this.itemWidth}px`,
      }
    },
    nextObserverOptions() {
      return {
        threshold: 0,
        root: this.$refs.datesWrapper,
        rootMargin: `0px ${this.itemWidth}px 0px 0px`,
      }
    },
  },
  watch: {
    $props: {
      immediate: true,
      handler() {
        this.validateMinMaxDates()
        this.validateInitialValues()
      },
    },
    windowWidth: {
      handler(newVal, oldVal) {
        if (oldVal > newVal) {
          return
        }

        this.fillCalendar()
      },
    },
  },
  mounted() {
    // adding calendar scroll on mousewheel
    const datesWrapperEl = this.$refs.datesWrapper

    if (this.enableMousewheelScroll) {
      datesWrapperEl.addEventListener('wheel', e => {
        e.preventDefault()

        datesWrapperEl.scrollBy({
          left: (e.deltaY < 0 ? -this.itemWidth : this.itemWidth) * this.scrollSpeed,
          behavior: 'smooth',
        })
      })
    }

    window.addEventListener('resize', this.onResize)

    // getting initial list of dates
    this.fillCalendar()

    // select today's date as initial
    this.$nextTick(() => {
      const todayItem = this.$el.querySelector('.date-item.today')
      todayItem.scrollIntoView({
        inline: 'start',
      })
    })
  },
  beforeDestroy() {
    if (this.enableMousewheelScroll) {
      const datesWrapperEl = this.$refs.datesWrapper
      datesWrapperEl.replaceWith(datesWrapperEl.cloneNode(true))
    }

    window.removeEventListener('resize', this.onResize)
  },
  methods: {
    fillCalendar() {
      if (this.dates.length) {
        this.dates = []
      }

      const rangeInitial = Math.ceil(this.windowWidth / ((this.itemWidth - this.itemsGap) * 2))
      this.getNextDatesInRange(new Date(), rangeInitial, false)
      this.getPrevDatesInRange(new Date(), rangeInitial, true)
    },
    getPrevDatesInRange(startDate, days, excludeFirstDate = false) {
      const date = new Date(startDate.getTime())

      if (excludeFirstDate) {
        date.setDate(date.getDate() - 1)
      }

      const dateCopy = new Date(date.getTime())
      const beginningDate = new Date(dateCopy.setDate(dateCopy.getDate() - days))

      let dates = []

      while (date > beginningDate) {
        dates.unshift(new Date(date))
        date.setDate(date.getDate() - 1)

        if (this.specMinDate) {
          dates = dates.filter(d => d > this.specMinDate || checkSameDay(d, this.specMinDate))
          this.showFirstObserver = dates.length >= days
        }
      }

      this.dates = this.dates.length ? [...dates, ...this.dates] : dates

      // eslint-disable-next-line prefer-destructuring
      this.minDate = this.dates[0]

      // keeping scroll position on adding elements to the beginning of the list
      const datesWrapperEl = this.$refs.datesWrapper
      const currScrollPos = datesWrapperEl.scrollLeft
      const oldScroll = datesWrapperEl.scrollWidth - datesWrapperEl.clientWidth

      this.$nextTick(() => {
        const newScroll = datesWrapperEl.scrollWidth - datesWrapperEl.clientWidth
        datesWrapperEl.scrollLeft = currScrollPos + (newScroll - oldScroll)
      })
    },
    getNextDatesInRange(startDate, days, excludeFirstDate = false) {
      const date = new Date(startDate.getTime())

      if (excludeFirstDate) {
        date.setDate(date.getDate() + 1)
      }

      const dateCopy = new Date(date.getTime())
      const endDate = new Date(dateCopy.setDate(dateCopy.getDate() + days))

      let dates = []

      while (date < endDate) {
        dates.push(new Date(date))
        date.setDate(date.getDate() + 1)

        if (this.specMaxDate) {
          dates = dates.filter(d => d < this.specMaxDate || checkSameDay(d, this.specMaxDate))
          this.showLastObserver = dates.length >= days
        }
      }

      this.dates = this.dates.length ? [...this.dates, ...dates] : dates
      this.maxDate = this.dates[this.dates.length - 1]
    },
    dateClickHandler(date) {
      if (this.isRange) {
        // eslint-disable-next-line no-unused-expressions
        this.startDate ? this.setEndDate(date) : this.setStartDate(date)
        return
      }

      this.setActiveDate(date)
    },
    // eslint-disable-next-line consistent-return
    setActiveDate(date) {
      if (!this.canSelectDate) {
        return false
      }

      this.selectedDate = date
      this.$emit('select-date', date)
    },
    // eslint-disable-next-line consistent-return
    setStartDate(date) {
      if (!this.canSelectDate) {
        return false
      }

      this.startDate = date
    },
    setEndDate(date) {
      if (!this.canSelectDate) {
        return
      }

      if (this.startDate === date || this.endDate === date) {
        this.startDate = date
        this.endDate = null
        return
      }

      this.endDate = date

      if (this.startDate > this.endDate) {
        [this.startDate, this.endDate] = [this.endDate, this.startDate]
      }

      this.$emit('select-dates-range', { startDate: this.startDate, endDate: this.endDate })
    },
    enableDateSelection() {
      setTimeout(() => {
        this.canSelectDate = true
      }, 0)
    },
    disableDateSelection(e) {
      if (e.detail.deltaX === -1 || Object.is(e.detail.deltaX, -0)) {
        return
      }

      this.canSelectDate = false
    },
    onResize() {
      this.windowWidth = window.innerWidth
    },
    validateMinMaxDates() {
      if (this.minDate > this.maxDate) {
        console.error('minDate cannot be greater than maxDate')
      }

      return true
    },
    validateInitialValues() {
      if (this.isRange && this.selectedDate) {
        console.error('You should use initialRange property with isRange')
        return
      }

      if (!this.isRange && this.startDate && this.endDate) {
        console.error('You should use initialDate property with default mode')
      }
    },
  },
}
</script>

<style>
@import '~@/assets/css/inline-calendar.css';
</style>
